<template>
  <v-layout class="fill-height bg-full">
    <!-- Toolbar -->
    <v-app-bar
      mode="floating"
      flat
      class="app-bar-full background-custom elevation-0 position-fixed"
    >
      <v-card
        class="flex-grow-1 d-flex pa-1 ps-0 background-custom elevation-0"
        tile
        flat
      >
        <div class="d-flex flex-grow-1 align-center">
          <div class="d-flex ps-1 align-center clickable-simple">
            <img
              :src="isThemeDark ? '/images/logo_light.png' : '/images/logo_dark.png'"
              height="48"
              alt="logo"
              class="me-1"
              @click="$router.push({ name: 'home' })"
            >
          </div>

          <div class="ms-2 d-flex align-center w-100 px-8">
            <h2>
              {{ routeHeaderName }}
              <v-btn
                v-if="routeIsDetails"
                icon
                size="22"
                :disabled="!formValid"
                color="primary"
                class="ms-5"
                data-cy="save-btn"
                @click="bus.$emit('saveResource', $route.name)"
              >
                <v-icon size="22">
                  mdi-content-save-outline
                </v-icon>
              </v-btn>
            </h2>
            <v-alert
              v-if="user && !isMfaEnabledForUser && userSettings.mfaStartingDate"
              type="warning"
              class="me-5 cursor-pointer"
              @click="$router.push({ name: 'profile' })"
            >
              {{ $lang.labels.afterMfaStartingdateYouWontBeAbleToLogin }}
            </v-alert>
            <editing-users-header />
          </div>

          <v-spacer />

          <div class="last-edited-hide align-center me-2" style="height: 100%">
            <header-tag v-if="isLoggedIn && $route.name !== 'set-password'" class="me-2" />
            <header-search v-if="isLoggedIn && $route.name !== 'set-password'" />
            <div v-if="isLoggedIn && $route.name !== 'set-password'" class="ms-1 me-1" />
            <header-history
              v-if="savedLocations && savedLocations.length > 0 && isLoggedIn && $route.name !== 'set-password'"
            />
          </div>

          <div class="me-1">
            <header-lang @change-lang="changeLang" />
          </div>

          <div v-if="isLoggedIn && $route.name !== 'set-password'" class="me-1">
            <header-user @change-theme="changeTheme" />
          </div>
        </div>
      </v-card>
    </v-app-bar>

    <!-- Navigation -->
    <v-navigation-drawer
      v-if="isLoggedIn && $route.name !== 'set-password'"
      v-model="drawer"
      permanent
      :rail="rail"
      mode="floating"
      floating
      location="start"
      width="210"
      class="background-custom drawer-wrapper position-fixed"
      @click="rail = false"
    >
      <flowy-main-menu
        :menu="menuItems"
        :rail="rail"
        @toggle-rail="rail = !rail"
      />

      <template #append>
        <MemoryCPUStats :rail="rail" />
      </template>
    </v-navigation-drawer>

    <v-container
      :fluid="!isContentBoxed"
      :style="containerStyle"
      :class="containerClass"
    >
      <router-view
        v-if="!loadingProfile"
        :rail="rail"
        :class="{'fill-height': true }"
      />
    </v-container>
  </v-layout>
</template>

<script>
import { debounce } from 'lodash'
import config from '@/configs'
import FlowyMainMenu from '@/components/menu/FlowyMainMenu'
import HeaderUser from '@/components/header/HeaderUser'
import HeaderLang from '@/components/header/HeaderLang'
import HeaderSearch from '@/components/header/HeaderSearch'
import HeaderHistory from '@/components/header/HeaderHistory'
import auth from '@/auth/index'
import { mapActions, mapGetters } from 'vuex'
import colorsConfig from '@/configs/colorSchemes'
import { getAllUserSettingsUsingGET as getProfile, saveUserSettingsUsingPOST as saveProfile } from '@/utils/api'
import { RTL_LANGUAGES } from '@/utils/constants'
import { VUETIFY_LOCALE_MAP } from '@/plugins/vuetify'
import { bus } from '@/main'
import EditingUsersHeader from '@/components/ui/EditingUsersHeader'
import MemoryCPUStats from "@/components/ui/MemoryCPUStats.vue";
import HeaderTag from '@/components/header/HeaderTag'

export default {
  components: {
    MemoryCPUStats,
    FlowyMainMenu,
    HeaderUser,
    HeaderLang,
    HeaderTag,
    HeaderSearch,
    HeaderHistory,
    EditingUsersHeader
  },
  inject: ['theme'],
  props: {
    isDemo: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loadingProfile: false,
      menuLoaded: false,
      darkModeMediaQuery: null,
      lastEditValue: '',
      lastEditValueSearch: '',
      frontEnd: '',
      frontEndBuildTime: '',
      backEnd: {
        branch: '',
        commit: {
          id: '',
          time: ''
        }
      },
      isLoggedIn: false,
      config,
      drawer: null,
      rail: false,
      isContentBoxed: false,
      user: localStorage.userData ? JSON.parse(localStorage.userData) : null
    }
  },
  computed: {
    bus() {
      return bus
    },
    ...mapGetters('app', {
      savedLocations: 'savedLocations',
      userSettings: 'userSettings',
      userRoles: 'userRoles',
      formValid: 'formValid',
      defaultFrontend: 'defaultFrontend',
      isRTL: 'isRTL',
      favorites: 'favorites'
    }),
    isMfaEnabledForUser() {
      return Boolean(this.user?.twoFactorAuth)
    },
    routeHeaderName() {
      const routeName = this.$route.name
      if (!routeName) return ''

      // Check if there's a localized route name
      if (this.$lang.routes[routeName]) {
        return this.$lang.routes[routeName]
      }

      // Fallback to string transformation for routes without localization
      return routeName.replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/edit$/i, 'Details')
        // Replace dashes with spaces
        .replace(/-/g, ' ')
        // Capitalize the first letter of each word
        .replace(/\b\w/g, (char) => char.toUpperCase())
    },
    routeIsDetails() {
      // List of pages that have edit and create pages where we want to show the save button
      const isInSelectedPages = [
        'rest',
        'mcp',
        'cron',
        'messaging',
        'whatsapp',
        'eventHandler',
        'trigger',
        'process',
        'credential',
        'validation',
        'entity',
        'template',
        'translation',
        'storage',
        'plugin',
        'staticResource',
        'library',
        'module',
        'user',
        'role',
        'setting',
        'tag',
        'import-history',
        'application',
        'aiAgent',
        'screen',
        'flow'
      ]
        .some((page) => this.$route.name ? this.$route.name.toLowerCase().includes(page.toLowerCase()) : false)

      // Check if the current page is an edit or create page
      return this.$route.name ? isInSelectedPages && (this.$route.name.toLowerCase().includes('edit') || this.$route.name.toLowerCase().includes('create')) : false
    },
    menuItems() {
      const favoriteItems = this.buildFavoriteItems()

      // Build favorites as a menu item with icon and items for flyout support in rail mode
      const favoritesMenuItem = favoriteItems.length ? {
        icon: 'mdi-star',
        key: 'menu.favorites',
        text: this.$lang.routes.favorites,
        items: favoriteItems
      } : null

      return [{
        text: '',
        items: [
          {
            icon: 'mdi-view-dashboard-outline',
            key: 'menu.dashboard',
            text: this.$lang.routes.home,
            link: 'home',
            exact: true,
            noFavorite: true
          },
          // Conditionally include favorites (only when non-empty)
          ...(favoritesMenuItem ? [favoritesMenuItem] : [])
        ]
      }, {
        key: 'menu.pages',
        items: this.baseMenuPages
      }]
    },
    isThemeDark() {
      return this.theme.global.name.value === 'dark'
    },
    tenantId() {
      const userData = JSON.parse(localStorage.userData || '{}')
      return (userData?.tenantId || 'DEFAULT').toString().toUpperCase()
    },
    containerStyle() {
      if (this.$route.name === 'home') {
        return 'padding: 0 !important; min-width: 0;'
      }
      return 'padding-top: 70px; padding-bottom: 0; min-width: 0;'
    },
    containerClass() {
      // Only apply margin when logged in and nav drawer is visible
      const showDrawer = this.isLoggedIn && this.$route.name !== 'set-password'
      if (!showDrawer) {
        return {}
      }
      // Apply margin based on rail state and RTL direction
      // Use explicit margin-left/margin-right since position:fixed drawer doesn't follow document flow
      const drawerWidth = this.rail ? 56 : 210
      if (this.isRTL) {
        return { 'mr-drawer': true, [`mr-${drawerWidth}`]: true }
      }
      return { 'ml-drawer': true, [`ml-${drawerWidth}`]: true }
    },
    // Base menu pages that can be favorited (excludes Home which is always visible)
    baseMenuPages() {
      return [
        {
          icon: 'mdi-rocket-launch', key: 'menu.trigger', text: this.$lang.routes.triggers, regex: 'triggers',
          items: [
            { icon: 'mdi-briefcase-clock-outline', key: 'menu.cron', text: this.$lang.routes.cron, link: 'cron' },
            { icon: 'mdi-briefcase-outline', key: 'menu.event-handler', text: this.$lang.routes.eventHandler, link: 'eventHandler' },
            { icon: 'mdi-message-text-outline', key: 'menu.messaging', text: this.$lang.routes.messaging, link: 'messaging' },
            { icon: 'mdi-server', key: 'menu.mcp', text: this.$lang.routes.mcp, link: 'mcp' },
            { icon: 'mdi-web', key: 'menu.rest', text: this.$lang.routes.rest, link: 'rest' },
            { icon: 'mdi-gesture-tap', key: 'menu.uiTrigger', text: this.$lang.routes.uiTrigger, link: 'uiTrigger' },
            { icon: 'mdi-whatsapp', key: 'menu.whatsapp', text: this.$lang.routes.whatsapp, link: 'whatsapp' }
          ]
        },
        {
          icon: 'mdi-play-circle-outline', key: 'menu.workflow', text: this.$lang.routes.workflow, regex: 'workflow|storage',
          items: [
            { icon: 'mdi-application', key: 'menu.applications', text: this.$lang.routes.applications, link: 'applications' },
            { icon: 'mdi-robot-happy', key: 'menu.aiAgents', text: this.$lang.routes.aiAgents, link: 'ai-agents' },
            { icon: 'mdi-database-settings-outline', key: 'menu.entities', text: this.$lang.routes.entities, link: 'entities' },
            { icon: 'mdi-transit-connection-variant', key: 'menu.flow', text: this.$lang.routes.flows, link: 'flow' },
            { icon: 'mdi-console-network', key: 'menu.processes', text: this.$lang.routes.processes, link: 'processes' },
            { icon: 'mdi-television', key: 'menu.screen', text: this.$lang.routes.screen, link: 'screen' },
            { icon: 'mdi-cube', key: 'menu.storage', text: this.$lang.routes.storage, link: 'storage' },
            { icon: 'mdi-magnify-scan', key: 'menu.validations', text: this.$lang.routes.validations, link: 'validations' }
          ]
        },
        {
          icon: 'mdi-package', key: 'menu.assets', text: this.$lang.routes.assets, regex: 'assets|templates|translations',
          items: [
            { icon: 'mdi-library-outline', key: 'menu.libraries', text: this.$lang.routes.libraries, link: 'libraries' },
            { icon: 'mdi-puzzle-outline', key: 'menu.plugins', text: this.$lang.routes.plugins, link: 'plugins' },
            { icon: 'mdi-image', key: 'menu.staticResources', text: this.$lang.routes.staticResources, link: 'static-resources' },
            { icon: 'mdi-tag', key: 'menu.tags', text: this.$lang.routes.tags, link: 'tags' },
            { icon: 'mdi-text-box-outline', key: 'menu.templates', text: this.$lang.routes.templates, link: 'templates' },
            { icon: 'mdi-text', key: 'menu.translations', text: this.$lang.routes.translations, link: 'translations' }
          ]
        },
        {
          icon: 'mdi-briefcase-edit-outline', key: 'menu.modules', text: this.$lang.routes.modules, regex: 'modules',
          items: [
            { icon: 'mdi-briefcase-edit-outline', key: 'menu.local', text: this.$lang.routes.local, link: 'local' },
            { icon: 'mdi-briefcase-download-outline', key: 'menu.hub', text: this.$lang.routes.hub, link: 'hub' },
            { icon: 'mdi-application-import', key: 'menu.importHistory', text: this.$lang.routes.importHistory, link: 'importHistory' }
          ]
        },
        {
          icon: 'mdi-account-box-outline', key: 'menu.access', text: this.$lang.routes.access, regex: 'access',
          hide: this.roleChecker(['USER_CREATOR', 'ROLE_CREATOR', 'CREDENTIAL_CREATOR', 'SUPER_USER']),
          items: [
            { icon: 'mdi-account', key: 'menu.users', text: this.$lang.routes.users, link: 'users', hide: this.roleChecker(['USER_CREATOR', 'SUPER_USER']) },
            { icon: 'mdi-account-group-outline', key: 'menu.roles', text: this.$lang.routes.roles, link: 'roles', hide: this.roleChecker(['ROLE_CREATOR', 'SUPER_USER']) },
            { icon: 'mdi-home-group', key: 'menu.tenants', text: this.$lang.routes.tenants, link: 'tenants', hide: this.tenantHideChecker() },
            { icon: 'mdi-lock', key: 'menu.cred', text: this.$lang.routes.credentials, link: 'credentials', hide: this.roleChecker(['CREDENTIAL_CREATOR', 'SUPER_USER']) }
          ]
        },
        {
          icon: 'mdi-file-clock-outline', key: 'menu.logs', text: this.$lang.routes.logs, regex: 'logs',
          items: [
            {
              icon: 'mdi-debug-step-into',
              key: 'menu.logsSteps',
              text: this.$lang.routes.logsSteps,
              link: 'logs-steps'
            },
            { icon: 'mdi-note', key: 'menu.logsEvents', text: this.$lang.routes.logsEvents, link: 'logs-events' },
            {
              icon: 'mdi-factory',
              key: 'menu.logsInstances',
              text: this.$lang.routes.instances,
              link: 'logs-instances',
              hide: this.roleChecker(['INSTANCE_ADMIN', 'SUPER_USER'])
            },
            {
              icon: 'mdi-engine',
              key: 'menu.internalJobs',
              text: this.$lang.labels.internalJobs,
              link: 'logs-internal-jobs'
            },
            {
              icon: 'mdi-marker',
              key: 'menu.telemetry',
              text: this.$lang.routes.telemetries,
              link: 'logs-telemetry',
              hide: this.roleChecker(['TELEMETRY_ADMIN', 'SUPER_USER'])
            }
          ]
        },
        {
          icon: 'mdi-cog', key: 'menu.settings', text: this.$lang.routes.settings, regex: 'settings',
          items: [
            {
              icon: 'mdi-cog-outline',
              key: 'menu.settings.global',
              text: this.$lang.routes.global,
              link: 'globalSettings',
              hide: this.roleChecker(['GLOBAL_SETTING', 'SUPER_USER'])
            },
            {
              icon: 'mdi-flip-to-back',
              key: 'menu.settings.instance.processing',
              text: this.$lang.routes.instanceProcessing,
              link: 'instance-processing'
            },
            {
              icon: 'mdi-flip-to-back',
              key: 'menu.settings.instance.admin',
              text: this.$lang.routes.instanceAdmin,
              link: 'instance-settings'
            },
            {
              icon: 'mdi-flip-vertical',
              key: 'menu.settings.process',
              text: this.$lang.routes.process,
              link: 'process'
            },
            {
              icon: 'mdi-puzzle-star-outline',
              key: 'menu.settings.plugin',
              text: this.$lang.routes.plugins,
              link: 'plugin'
            }
          ]
        },
        {
          icon: 'mdi-tools', key: 'menu.admin', text: this.$lang.routes.admin, regex: 'admin',
          items: [
            { icon: 'mdi-database-search-outline', key: 'menu.browseData', text: this.$lang.routes.browseData, link: 'browseData' },
            { icon: 'mdi-radar',
              key: 'menu.mass.changes',
              text: this.$lang.routes.massChanges,
              link: 'mass-changes'
            },
            { icon: 'mdi-history',
              key: 'menu.history',
              text: this.$lang.routes.history,
              link: 'history'
            },
            { icon: 'mdi-safe', key: 'menu.gdpr', text: this.$lang.routes.gdpr, link: 'gdpr', hide: this.roleChecker(['GDPR_ADMIN', 'SUPER_USER']) },
            { icon: 'mdi-cached', key: 'menu.cache', text: this.$lang.routes.cache, link: 'cache', hide: this.roleChecker(['FLOWY_ADMIN', 'SUPER_USER']) }
          ]
        },
        {
          icon: 'mdi-bookmark', key: 'menu.links', text: this.$lang.routes.links,
          items: [
            {
              icon: 'mdi-code-json',
              key: 'menu.swagger',
              text: this.$lang.labels.swagger,
              link: '/api/swagger-ui/index.html',
              fullLink: true,
              specKey: 'swagger'
            },
            {
              icon: 'mdi-ticket',
              key: 'menu.serviceDesk',
              text: this.$lang.labels.serviceDesk,
              link: 'https://damudo.atlassian.net/servicedesk/',
              fullLink: true,
              specKey: 'serviceDesk'
            },
            {
              icon: 'mdi-note-text-outline',
              key: 'menu.logsServer',
              text: this.$lang.labels.logsServer,
              link: this.defaultFrontend.logServerUrl,
              fullLink: true,
              specKey: 'logsServer'
            },
            {
              icon: 'mdi-format-list-checkbox',
              key: 'menu.metricServer',
              text: this.$lang.labels.metricServer,
              link: this.defaultFrontend.metricServerUrl,
              fullLink: true,
              specKey: 'metricServer'
            },
            {
              icon: 'mdi-chart-box-outline',
              key: 'menu.visualisationServer',
              text: this.$lang.labels.visualisationServer,
              link: this.defaultFrontend.visualisationServerUrl,
              fullLink: true,
              specKey: 'visualisationServer'
            }
          ]
        }
      ]
    },
    // Cached lookup map for route links to full menu item objects
    menuItemMap() {
      const map = {}
      const processItem = (item) => {
        if (item.items) {
          item.items.forEach(processItem)
        } else if (item.link && !item.fullLink) {
          map[item.link] = {
            icon: item.icon,
            text: item.text,
            link: item.link,
            key: item.key
          }
        }
      }
      // Process all menu items from baseMenuPages (Home is NOT included - it's always visible)
      this.baseMenuPages.forEach(processItem)
      return map
    }
  },
  mounted() {
    // Mark menu as loaded for favorites cleanup guard
    this.menuLoaded = true

    // Run deferred cleanup if favorites were loaded before mount
    if (this.userSettings?.favorites?.length && Object.keys(this.menuItemMap).length) {
      const validRoutes = new Set(Object.keys(this.menuItemMap))
      const currentFavorites = this.userSettings.favorites
      const cleanedFavorites = currentFavorites.filter(link => validRoutes.has(link))

      if (cleanedFavorites.length !== currentFavorites.length) {
        console.debug('mounted: running deferred favorites cleanup')
        this.$store.commit('app/setFavorites', cleanedFavorites)
        this.$nextTick(() => this.debouncedSaveUserProfile())
      }
    }

    this.darkModeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    this.darkModeMediaQuery.addEventListener('change', this.colorThemeListener)

    this.frontEnd = localStorage.frontendVersion ? localStorage.frontendVersion : ''

    this.frontEndBuildTime = localStorage.frontendBuildtime ? localStorage.frontendBuildtime : ''

    if (localStorage.backendVersion) {
      try {
        this.backEnd = JSON.parse(localStorage.backendVersion)
      } catch {
        localStorage.removeItem('backendVersion')
      }
    }

    const userDataObj = localStorage.userData ? JSON.parse(localStorage.userData) : false

    auth.userData.loggedInStatus = !!userDataObj.authToken
    auth.userData.token = userDataObj.authToken
    auth.userData.tenantId = userDataObj.tenantId

    this.isLoggedIn = !!userDataObj.authToken
  },
  created() {
    // 1. Create debounced save function FIRST - fetchUserProfile() may need it
    this.debouncedSaveUserProfile = debounce(this.saveUserProfile, 500)

    // 2. Set up event bus listener for external triggers (from Vuex action)
    bus.$on('saveUserProfile', this.debouncedSaveUserProfile)

    // 3. Now proceed with existing created logic (theme detection, fetchUserProfile, etc.)
    const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches

    if (auth.isLoggedIn()) {
      this.fetchUserProfile()
    } else {
      if (localStorage.selectedTheme && JSON.parse(localStorage.selectedTheme) !== isDark) {
        this.theme.global.name.value = JSON.parse(localStorage.selectedTheme) ? 'dark' : 'light'
        this.globalTheme(JSON.parse(localStorage.selectedTheme))
      } else {
        this.theme.global.name.value = isDark ? 'dark' : 'light'
        localStorage.setItem('selectedTheme', JSON.stringify(isDark))
        this.globalTheme(isDark)
      }
    }
  },
  beforeUnmount() {
    this.debouncedSaveUserProfile?.cancel()
    bus.$off('saveUserProfile')
    this.darkModeMediaQuery?.removeEventListener('change', this.colorThemeListener)
  },
  methods: {
    ...mapActions('app', [
      'globalTheme',
      'userSettingsSave',
      'addSnackbar'
    ]),
    buildFavoriteItems() {
      return this.favorites
        .map(link => this.menuItemMap[link])
        .filter(Boolean) // Remove any that weren't found (deleted routes)
    },
    tenantHideChecker() {
      const isDefaultTenant = this.tenantId === 'DEFAULT'

      if (!isDefaultTenant) return true

      return this.roleChecker(['TENANT_CREATOR', 'SUPER_USER'])
    },
    roleChecker(rolesArray) {
      return !(this.userRoles && this.userRoles.some((role) => rolesArray.includes(role.name)))
    },
    fetchUserProfile() {
      this.loadingProfile = true
      getProfile()
        .then((res) => {
          const tempData = res.data.data

          if (!tempData.display) tempData.display = {
            showCreatedOn: true,
            showModifiedOn: true,
            showId: true,
            showComments: false,
            showCopyPaste: false
          }

          // Initialize favorites if not present
          if (!tempData.favorites) tempData.favorites = []

          // Guard: Only cleanup stale favorites after menu is fully loaded
          // menuLoaded is set in mounted() after Vue's reactive system has computed menuItemMap
          if (this.menuLoaded) {
            const validRoutes = new Set(Object.keys(this.menuItemMap))
            const cleanedFavorites = tempData.favorites.filter(link => validRoutes.has(link))
            const hadStaleEntries = cleanedFavorites.length !== tempData.favorites.length
            tempData.favorites = cleanedFavorites

            this.userSettingsSave({...this.userSettings, ...tempData})

            if (hadStaleEntries) {
              this.$nextTick(() => this.debouncedSaveUserProfile())
            }
          } else {
            // Menu not yet mounted, defer cleanup to avoid race condition
            console.debug('fetchUserProfile: deferring favorites cleanup until menuLoaded')
            this.userSettingsSave({...this.userSettings, ...tempData})
          }

          const localTheme = localStorage.selectedTheme ? JSON.parse(localStorage.selectedTheme) : null

          if (tempData.visuals?.selectedColorScheme) this.changeColors(tempData.visuals.selectedColorScheme)
          if (tempData.visuals?.selectedTheme) this.changeTheme(localTheme !== null ? localTheme : tempData.visuals.selectedTheme === 'dark')
          if (tempData.general?.selectedLanguage) this.changeLang(tempData.general.selectedLanguage)

          this.loadingProfile = false
        })
        .catch((err) => {
          console.log(err)
          this.loadingProfile = false
        })
    },
    saveUserProfile() {
      saveProfile({
        body: {
          settings: {
            general: {
              selectedLanguage: localStorage.selectedLanguage
            },
            visuals: {
              selectedTheme: localStorage.selectedTheme ? JSON.parse(localStorage.selectedTheme) ? 'dark' : 'light' : 'light',
              selectedColorScheme: localStorage.selectedColorScheme ? localStorage.selectedColorScheme : 'light'
            },
            display: this.userSettings.display,
            favorites: this.userSettings.favorites || []
          }
        }
      }).catch((err) => {
        console.error('Failed to save user profile:', err)
        this.addSnackbar({
          message: this.$lang.errors.profileSaveFailed || 'Failed to save preferences',
          color: 'warning',
          timeout: 5000
        })
      })
    },
    async changeLang(lang) {
      const previousLang = this.$current_lang
      const isDifferent = lang !== localStorage.selectedLanguage

      // $setLang is now async - returns { success, timedOut }
      const result = await this.$setLang(lang)

      if (result.success) {
        // Only update RTL and save after successful language load
        const isRTL = RTL_LANGUAGES.includes(lang)
        this.$store.commit('app/setRTL', isRTL)

        localStorage.setItem('selectedLanguage', lang)

        // Update Vuetify's locale for component translations and RTL support
        if (this.$vuetify?.locale) {
          if (VUETIFY_LOCALE_MAP[lang] || isRTL) {
            // Use lang directly if Vuetify has translations or it's RTL (for RTL layout)
            this.$vuetify.locale.current = lang
          } else {
            // For LTR languages without Vuetify support, use 'en' to ensure LTR mode
            this.$vuetify.locale.current = 'en'
          }
        }

        if (isDifferent) this.debouncedSaveUserProfile()
        if (this.$route.params.lang && this.$route.params.lang !== lang) {
          this.$router.push({ name: this.$route.name, params: { lang } })
        }
      } else {
        // Language load failed - show error notification with timeout-specific message
        console.warn(`Failed to load language ${lang}, keeping ${previousLang}`)
        const errorKey = result.timedOut ? 'languageLoadTimeout' : 'languageLoadFailed'
        this.addSnackbar({
          message: this.$t(`errors.${errorKey}`),
          color: 'error',
          timeout: 5000
        })
      }
    },
    changeTheme(isNewThemeDark) {
      const isDifferent = String(isNewThemeDark) !== localStorage.selectedTheme

      this.theme.global.name.value = isNewThemeDark ? 'dark' : 'light'
      localStorage.setItem('selectedTheme', isNewThemeDark)
      if (isDifferent) this.debouncedSaveUserProfile()
      this.globalTheme(isNewThemeDark)
    },
    changeColors(type) {
      const { dark } = colorsConfig.themes[type]
      const { light } = colorsConfig.themes[type]

      if (dark && light) {
        localStorage.setItem('selectedColorScheme', type)
        // set themes
        Object.keys(dark).forEach((i) => {
          this.theme.themes.value.dark[i] = dark[i]
        })
        Object.keys(light).forEach((i) => {
          this.theme.themes.value.light[i] = light[i]
        })
      }
    },
    colorThemeListener(event) {
      if (event.matches) {
        this.$vuetify.theme.dark = true
        localStorage.setItem('selectedTheme', 'true')
      } else {
        this.$vuetify.theme.dark = false
        localStorage.setItem('selectedTheme', 'false')
      }
    }
  }
}
</script>

<style lang="scss">
.app-bar-full {
  .v-toolbar__content,
  .v-toolbar__extension {
    padding: 0;
    align-items: normal;
    overflow: visible;

    .v-card {
      overflow: visible;
    }
  }
}

.bg-full {
  background: rgb(var(--v-theme-customBg)) !important;
}

.last-edited-hide {
  display: flex !important;
}

@media screen and (max-width: 600px) {
  .last-edited-hide {
    display: none !important;
  }
}

.toggle-menu {
  .v-size--default {
    width: 28px !important;
    height: 28px !important;
  }
}

.v-navigation-drawer__content::-webkit-scrollbar-track {
  -webkit-box-shadow: inset 0 0 6px #5d5d5d;
  background-color: #5d5d5d;
}

.v-navigation-drawer__content::-webkit-scrollbar {
  width: 0;
}

.v-navigation-drawer__content::-webkit-scrollbar-thumb {
  -webkit-box-shadow: inset 0 0 6px #424242;
  background-color: #424242;
}

// Drawer margin classes for content container
.ml-drawer {
  transition: margin-left 0.2s ease;
}
.mr-drawer {
  transition: margin-right 0.2s ease;
}
.ml-56 {
  margin-left: 56px !important;
}
.ml-210 {
  margin-left: 210px !important;
}
.mr-56 {
  margin-right: 56px !important;
}
.mr-210 {
  margin-right: 210px !important;
}
</style>
